import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { GiCrossedBones } from "react-icons/gi";
import { LuSave } from "react-icons/lu";
import { Trash2 } from "lucide-react";

const AddHealthInsuranceQuotation = () => {
    const [selectedYears, setSelectedYears] = useState(1);
    const navigate = useNavigate();
    const [members, setMembers] = useState([
        {
            name: "", dob: "", relation: ""
        }
    ]);

    const validationSchema = Yup.object({
        // Policy Info
        insurer: Yup.string().required("Insurer is required"),
        product: Yup.string().required("Product is required"),
        policyHolder: Yup.string().required("Policy holder is required"),
        quotationNo: Yup.string().required("Quotation number is required"),
        quotationDate: Yup.string().required("Quotation date is required"),

        // Coverage Info
        plan: Yup.string().required("Plan is required"),
        sumInsurer: Yup.string().required("Sum insured is required"),

        // Members (at least one)
        members: Yup.array()
            .of(
                Yup.object({
                    name: Yup.string().required("Member name is required"),
                    dob: Yup.string().required("Date of birth is required"),
                    relation: Yup.string().required("Relation is required"),
                })
            )
            .min(1, "At least one member is required"),

        // Financial
        netPremium: Yup.string().required("Net premium is required"),
    });

    const handleAddMember = () => {
        setMembers([...members, { name: "", dob: "", relation: "" }]);
    };

    const handleRemoveMember = (index) => {
        const newMembers = [...members];
        newMembers.splice(index, 1);
        setMembers(newMembers);
    };

    const handleMemberChange = (index, field, value) => {
        const newMembers = [...members];
        newMembers[index][field] = value;
        setMembers(newMembers);
    };

    const handleSubmit = (values, { resetForm }) => {
        const formData = {
            ...values,
            members: members
        };
        console.log("Submitted:", formData);
        alert("Form Submitted!");
        resetForm();
        setMembers([]);
    };

    return (
        <div className="formContainer lg:p-4 rounded" style={{ boxShadow: "0 0 8px #ccc" }}>
            <Formik
                initialValues={{
                    insurer: "",
                    product: "",
                    policyHolder: "",
                    quotationNo: "",
                    quotationDate: "",
                    plan: "",
                    zone: "",
                    sumInsurer: "",
                    basicPremium: "",
                    addonPremium: "",
                    grossPremium: "",
                    gst: "",
                    netPremium: "",
                    notes: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, isSubmitting }) => (
                    <Form className="p-4 bg-white rounded">
                        <h5 className="text-brandRed font-bold border-b border-brandRed pb-2 mb-4">Health Insurance Quotation</h5>

                        {/* Header Section */}
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label">Insurer <span className="text-danger">*</span></label>
                                <Field as="select" name="insurer" className="form-select custom-shadow">
                                    <option value="">Select</option>
                                    <option value="HDFC Ergo">HDFC Ergo</option>
                                    <option value="Tata AIG">Tata AIG</option>
                                </Field>
                                <ErrorMessage name="insurer" component="div" className="text-danger" />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Product <span className="text-danger">*</span>
                                </label>
                                <Field as="select" name="product" className="form-select custom-shadow">
                                    <option value="">Select</option>
                                    <option value="Health Suraksha">Health Suraksha</option>
                                    <option value="MediCare">MediCare</option>
                                </Field>
                                <ErrorMessage name="product" component="div" className="text-danger" />
                            </div>
                            <div className="col-md-5">
                                <label className="form-label">Policy Holder <span className="text-danger">*</span>
                                </label>
                                <Field as="select" name="policyHolder" className="form-select custom-shadow">
                                    <option value="">Select</option>
                                    <option value="Rahul Sharma">Rahul Sharma</option>
                                    <option value="Priya Verma">Priya Verma</option>
                                </Field>
                                <ErrorMessage name="policyHolder" component="div" className="text-danger" />
                            </div>
                            <div className="col-md-1 mt-2 mt-md-5">
                                <button
                                    type="button"
                                    className="backButton"
                                    onClick={() => navigate("/customer-master/add")}
                                >
                                    Add
                                </button>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Quotation No <span className="text-danger">*</span>
                                </label>
                                <Field name="quotationNo" className="form-control custom-shadow" />
                                <ErrorMessage name="quotationNo" component="div" className="text-danger" />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Quotation Date <span className="text-danger">*</span>
                                </label>
                                <Field type="date" name="quotationDate" className="form-control custom-shadow" />
                                <ErrorMessage name="quotationDate" component="div" className="text-danger" />
                            </div>
                        </div>

                        {/* Product Information */}
                        <h6 className="text-secondary fw-bold mb-3 mt-5 bg-gray-100 rounded p-2">
                            Product Information</h6>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label">Plan <span className="text-danger">*</span>
                                </label>
                                <Field as="select" name="plan" className="form-select custom-shadow">
                                    <option value="">Select</option>
                                    <option value="Silver">Silver</option>
                                    <option value="Gold">Gold</option>
                                </Field>
                                <ErrorMessage name="plan" component="div" className="text-danger" />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Zone</label>
                                <Field name="zone" className="form-control custom-shadow" />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Sum Insurer <span className="text-danger">*</span>
                                </label>
                                <Field name="sumInsurer" className="form-control custom-shadow" />
                                <ErrorMessage name="sumInsurer" component="div" className="text-danger" />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">No of Years</label>
                                <div className="d-flex gap-2">
                                    {[1, 2, 3].map((year) => (
                                        <button
                                            key={year}
                                            type="button"
                                            className={`toggle-btn ${selectedYears === year ? "active" : ""}`}
                                            onClick={() => setSelectedYears(year)}
                                        >
                                            {year}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Member Information */}
                        <h6 className="text-secondary fw-bold mb-3 mt-5 bg-gray-100 rounded p-2">
                            Member Information</h6>

                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                            title="Member Name">Member Name</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                            title="Member DOB">Member DOB</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                            title="Member Relation">Relation</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                            title="Action">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((member, index) => (
                                        <tr key={index}>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="outline-none focus:none m-auto w-100"
                                                    value={member.name}
                                                    onChange={(e) => handleMemberChange(index, "name", e.target.value)}
                                                    required
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="date"
                                                    className="outline-none focus:none m-auto w-100"
                                                    value={member.dob}
                                                    onChange={(e) => handleMemberChange(index, "dob", e.target.value)}
                                                    required
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="outline-none focus:none m-auto w-100"
                                                    value={member.relation}
                                                    onChange={(e) => handleMemberChange(index, "relation", e.target.value)}
                                                    required
                                                />
                                            </td>
                                            <td>

                                                <button
                                                    className="delete-button fw-medium mx-1 p-1"
                                                    onClick={() => handleRemoveMember(index)}
                                                    title="Delete"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                >
                                                    <Trash2 size={18} color="#787474" className="mt-1" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <button
                                type="button"
                                className="saveButton"
                                onClick={handleAddMember}
                            >
                                + Add Member
                            </button>
                        </div>

                        {/* Premium Information */}
                        <h6 className="text-secondary fw-bold mb-3 mt-5 bg-gray-100 rounded p-2">
                            Premium Information</h6>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label">Basic Premium</label>
                                <Field name="basicPremium" className="form-control custom-shadow" />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Addon Premium</label>
                                <Field name="addonPremium" className="form-control custom-shadow" />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Gross Premium</label>
                                <Field name="grossPremium" className="form-control custom-shadow" />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">GST</label>
                                <Field name="gst" className="form-control custom-shadow" />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Net Premium <span className="text-danger">*</span>
                                </label>
                                <Field name="netPremium" className="form-control custom-shadow" />
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="mb-4 mt-2">
                            <label className="form-label">Notes</label>
                            <Field as="textarea" name="notes" rows="4" className="form-control custom-shadow" />
                        </div>

                        {/* Buttons */}
                        <div className="d-flex justify-content-center gap-3 mt-5">
                            <button type="submit" className="saveButton d-flex" disabled={isSubmitting}>
                                <span className="m-auto p-1">
                                    <LuSave />
                                </span>
                                {isSubmitting ? "Saving..." : "Save"}
                            </button>
                            <button
                                type="button"
                                className="backButton d-flex align-items-center"
                                onClick={() => window.history.back()}
                            >
                                <span className="m-auto p-1">
                                    <GiCrossedBones />
                                </span>
                                Cancel
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AddHealthInsuranceQuotation;